import { z } from 'zod';
import { adminApiFetch } from './apiService';
import { apiSuccess, apiList, parseAdminApiResponse, type JsonObject } from '@services/apiSchemas';
import {
  InventoryTransformationStatusSchema,
  InventoryTransformationItemStatusSchema,
  RuleSnapshotSchema,
  ReasonCodeSchema,
  FailureInterpolationsSchema,
} from '@services/inventoryTransformationSchemas';

// Schemas mirror Api::Admin::V1::InventoryTransformationsController
// (app/controllers/api/admin/v1/inventory_transformations_controller.rb).
//
// Enum / snapshot / reason_code schemas come from the shared module — they
// mirror Rails source-of-truth (model enums, REASON_CODES constant,
// SnapshotBuilder structure). List / detail row shapes are surface-specific
// (admin vs operator differ on emitted fields) and remain local.
//
// All object schemas use .passthrough() so backend additions don't break parsing
// — drift detection comes from missing or mistyped fields, not extras.

export { InventoryTransformationStatusSchema, InventoryTransformationItemStatusSchema };
export type InventoryTransformationStatus = z.infer<typeof InventoryTransformationStatusSchema>;
export type InventoryTransformationItemStatus = z.infer<typeof InventoryTransformationItemStatusSchema>;

// Row shape returned by GET get_records (controller line 27-40). Lean projection
// — no items, no counts. Don't confuse with the detail schema below.
const InventoryTransformationRowSchema = z.object({
  id: z.string(),
  name: z.string().nullable(),
  kind: z.string(),
  organization_name: z.string(),
  warehouse_name: z.string(),
  status: InventoryTransformationStatusSchema,
  note: z.string().nullable(),
  applicant_name: z.string().nullable(),
  approver_name: z.string().nullable(),
  created_at: z.string(),
}).passthrough();

// Applicant / approver sub-object on the detail payload (controller line 57-62
// + applicant_payload helper line 261-275). `kind` discriminates which of
// merchant_user / operator the actor is.
const PartyRefSchema = z.object({
  id: z.string(),
  name: z.string().nullable(),
  kind: z.enum([ 'merchant_user', 'operator' ]),
}).passthrough();

// Per-item shape on the detail payload (controller line 63-86). Source-side
// fields are nullable because source_inventory can be nil (e.g. items that
// failed before/without an inventory binding); target-side fields are nullable
// for the same reason on purpose_reallocation (no batch / expiration override).
// reason_code mirrors REASON_CODES (shared schema) — surface mismatches at parse.
const InventoryTransformationItemSchema = z.object({
  id: z.string(),
  product_name: z.string(),
  product_sku: z.string(),
  quantity: z.number(),
  reason_code: ReasonCodeSchema,
  source_batch: z.string().nullable(),
  target_batch: z.string().nullable(),
  source_expiration_date: z.string().nullable(),
  target_expiration_date: z.string().nullable(),
  source_manufacturing_date: z.string().nullable(),
  target_manufacturing_date: z.string().nullable(),
  source_inventory_type_name: z.string().nullable(),
  target_inventory_type_name: z.string().nullable(),
  source_custom_attributes: z.record(z.string(), z.unknown()),
  target_custom_attributes: z.record(z.string(), z.unknown()),
  note: z.string().nullable(),
  // New in partial-success spec ↓
  status: InventoryTransformationItemStatusSchema,
  failure_code: z.string().nullable(),
  failure_interpolations: FailureInterpolationsSchema,
  executed_at: z.string().nullable(),
}).passthrough();
export type InventoryTransformationItem = z.infer<typeof InventoryTransformationItemSchema>;

// Detail payload from GET /:id (controller line 47-91). Includes the new
// applied_count / failed_count / total_items_count rollups computed in-memory
// from the eager-loaded items association.
const InventoryTransformationDetailSchema = z.object({
  id: z.string(),
  name: z.string().nullable(),
  kind: z.string(),
  organization_name: z.string(),
  warehouse_name: z.string(),
  status: InventoryTransformationStatusSchema,
  note: z.string().nullable(),
  created_at: z.string(),
  triggered_by_rule_snapshot: RuleSnapshotSchema.nullable(),
  applicant: PartyRefSchema.nullable(),
  approver: PartyRefSchema.nullable(),
  items: z.array(InventoryTransformationItemSchema),
  applied_count: z.number().int().nonnegative(),
  failed_count: z.number().int().nonnegative(),
  total_items_count: z.number().int().nonnegative(),
}).passthrough();
export type InventoryTransformationDetail = z.infer<typeof InventoryTransformationDetailSchema>;

// suggest_name returns { name } (controller line 9-11). `name` can technically
// be a string — generate_suggested_name always produces a value — but use
// .nullable() for forward-compat with future "no suggestion" cases.
const SuggestNameSchema = z.object({
  name: z.string().nullable(),
}).passthrough();

// create returns a slim summary (controller line 155-160): only id / status /
// applied_count / failed_count. The page navigates to /:id immediately and
// re-fetches via show, so we don't need the full detail back here.
const InventoryTransformationCreatedSchema = z.object({
  id: z.string(),
  status: InventoryTransformationStatusSchema,
  applied_count: z.number().int().nonnegative(),
  failed_count: z.number().int().nonnegative(),
}).passthrough();

// Create payload mirrors create_items_params permit list (controller line
// 196-213). target_batch / target_expiration_date / target_manufacturing_date /
// target_custom_attributes are intentionally NOT permitted on admin
// self-service (purpose reallocation only changes inventory_type).
//
// `extends JsonObject` 讓 adminApiFetch body 接得到。JsonObject 嚴格不含 `undefined`
// （JSON spec 沒這個 value、JSON.stringify 對 undefined 是 silent drop key），所以
// optional field 一律寫 `T | null`（required + nullable），caller 在 construction
// 時用 `?? null` 顯式表態「沒填就送 null」。混 `?:` 會回到 `undefined` 漏出來、
// 滿足不了 JsonObject 的 index sig contract。
export interface InventoryTransformationCreateItem extends JsonObject {
  inventory_id: string;
  quantity: number;
  target_inventory_type_id: string;
  reason_code: string;
  note: string | null;
}

export interface InventoryTransformationCreateInput extends JsonObject {
  warehouse_id: string;
  name: string | null;
  note: string | null;
  items: InventoryTransformationCreateItem[];
}

export const inventoryTransformationService = {
  getRecords: async (params: Record<string, unknown>) => {
    const res = await adminApiFetch('inventory_transformations/get_records', {
      method: 'POST',
      body: JSON.stringify(params),
      headers: { 'Content-Type': 'application/json' },
    });
    return parseAdminApiResponse(res, apiList(InventoryTransformationRowSchema));
  },

  get: async (id: string) => {
    const res = await adminApiFetch(`inventory_transformations/${id}`);
    return parseAdminApiResponse(res, apiSuccess(InventoryTransformationDetailSchema));
  },

  suggestName: async () => {
    const res = await adminApiFetch('inventory_transformations/suggest_name');
    return parseAdminApiResponse(res, apiSuccess(SuggestNameSchema));
  },

  create: async (data: InventoryTransformationCreateInput) => {
    const res = await adminApiFetch('inventory_transformations', {
      method: 'POST',
      body: JSON.stringify(data),
      headers: { 'Content-Type': 'application/json' },
    });
    return parseAdminApiResponse(res, apiSuccess(InventoryTransformationCreatedSchema));
  },
};

export default inventoryTransformationService;

export type InventoryTransformationRow = z.infer<typeof InventoryTransformationRowSchema>;
export type InventoryTransformationCreated = z.infer<typeof InventoryTransformationCreatedSchema>;
